import { defineStore } from "pinia";
import { pb } from "@/utils/PocketBaseAdapter";
import type { PosSetupsResponse } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";

export const usePosSetupStore = defineStore("PosSetupStore", () => {
  const posSetup = ref<PosSetupsResponse | null>(null);
  const isLoading = ref(false);
  const isLoaded = ref(false);
  let fetchPromise: Promise<PosSetupsResponse | null> | null = null;

  const getPosId = async() => {
    let posId = localStorage.getItem("POS_SETUP");
    if(!posId){
      const { currentLocationDetails } = useUserStore();
      posId = currentLocationDetails?.defaultPosSetup;
    }
    if(!posId) return "";
    return posId;
  };

  // Returns cached record; joins any in-flight request; or starts new fetch.
  const load = async (): Promise<PosSetupsResponse | null> => {
    if (isLoaded.value) return posSetup.value;
    if (fetchPromise) return fetchPromise;

    fetchPromise = (async () => {
      try {
        isLoading.value = true;
        const posId = await getPosId();
        if (!posId) return null;

        const record = await pb.collection("posSetups").getOne<PosSetupsResponse>(posId, {
          requestKey: `posSetup-${posId}`
        });

        posSetup.value = record;
        isLoaded.value = true;
        return record;
      } catch (e) {
        console.error("PosSetupStore: failed to load posSetup", e);
        return null;
      } finally {
        isLoading.value = false;
        fetchPromise = null;
      }
    })();

    return fetchPromise;
  };

  // Call when POS_SETUP localStorage changes to force a fresh fetch.
  const refresh = async (): Promise<PosSetupsResponse | null> => {
    posSetup.value = null;
    isLoaded.value = false;
    fetchPromise = null;
    return load();
  };

  const reset = () => {
    posSetup.value = null;
    isLoading.value = false;
    isLoaded.value = false;
    fetchPromise = null;
  };

  // Convenience computed shortcuts
  const barCodeReader  = computed(() => posSetup.value?.barCodeReader  ?? "");
  const accessReader   = computed(() => posSetup.value?.accessReader   ?? "");
  const receiptPrinter = computed(() => posSetup.value?.receiptPrinter ?? "");

  return { 
    posSetup,
    load,
    refresh,
    reset,
    barCodeReader,
    accessReader,
    receiptPrinter
  };
});
